/**
 * CoverMediaImage – renders a cover image from media_assets.
 * Shows placeholder if no image exists. Never renders <img> without src.
 */
import { ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCoverAsset } from '@/hooks/useCoverAsset';
import type { OwnerType, VariantWidth } from '@/lib/media/mediaTypes';

interface CoverMediaImageProps {
  ownerType: OwnerType;
  ownerId: string | null | undefined;
  alt: string;
  className?: string;
  aspect?: string; // e.g. "16/9", "4/3"
  variant?: VariantWidth;
  /** Optional pre-resolved URL (from batch query) to avoid extra query */
  resolvedUrl?: string | null;
}

export function CoverMediaImage({
  ownerType,
  ownerId,
  alt,
  className,
  aspect = '16/9',
  variant = 640,
  resolvedUrl,
}: CoverMediaImageProps) {
  // Only run the individual query if no resolvedUrl was provided
  const { url640, url320 } = useCoverAsset(
    ownerType,
    resolvedUrl !== undefined ? undefined : ownerId,
  );

  const url = resolvedUrl ?? (variant >= 640 ? url640 : url320) ?? url640 ?? url320;

  if (!url) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/60',
          className,
        )}
        style={{ aspectRatio: aspect }}
      >
        <ImageIcon className="h-8 w-8 text-muted-foreground/40" />
        <p className="text-xs text-muted-foreground/60 mt-2">Ingen bild</p>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      className={cn('w-full h-full object-cover', className)}
      style={{ aspectRatio: aspect }}
      loading="lazy"
    />
  );
}
